import React, { useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { default as ReactSelect } from 'react-select';
import { components } from 'react-select';
import { DatePicker } from 'antd';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import { displayError } from '../helpers';
import SettingsIcon from './SettingsIcon';
import { getListViewSettings, saveListViewSettings } from '../services/SettingsServices';
import { getUserAllowedPlants } from '../services/PlantServices';
import {
  LIST_VIEW_SCHEMAS,
} from '../config/listViewSettingsConfig';
import { formatResolvedRange } from '../utils/listViewDateRanges';
import '../forms/admin/ListViewSettingsModal.css';

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <input type="checkbox" checked={props.isSelected} onChange={() => null} />{' '}
    <label>{props.label}</label>
  </components.Option>
);

const mandatorySelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderRadius: 8,
    borderLeft: '3px solid #dc2626',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#cbd5e1',
      borderLeftColor: '#dc2626',
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#dbeafe',
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#1e3a8a',
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#1e3a8a',
    ':hover': {
      backgroundColor: '#bfdbfe',
      color: '#1e3a8a',
    },
  }),
};

const dateRangeSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 8,
    borderColor: state.isFocused ? '#2563eb' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 1px #2563eb' : 'none',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#0f172a',
    fontWeight: 500,
  }),
};

function ListViewSettings({ viewKey, onSaved }) {
  const [cookies] = useCookies(['myToken']);
  const schema = viewKey ? LIST_VIEW_SCHEMAS[viewKey] : null;

  const [showModal, setShowModal] = useState(false);
  const [config, setConfig] = useState({});
  const [plantOptions, setPlantOptions] = useState([]);

  const resolvedRangeText = useMemo(
    () => formatResolvedRange(config, viewKey),
    [config, viewKey],
  );

  const loadModalData = () => {
    if (!viewKey) return;
    Promise.all([
      getListViewSettings(cookies, viewKey),
      getUserAllowedPlants(cookies),
    ])
      .then(([settingsResponse, plantResponse]) => {
        setConfig(settingsResponse.config || {});
        const plants = (plantResponse.plant_list || [])
          .filter((plant) => plant.status)
          .map((plant) => ({
            value: plant.id,
            label: plant.plant_alias || plant.entity_name,
          }));
        setPlantOptions(plants);
      })
      .catch((error) => {
        displayError(error.response?.data ?? error.message, 'Failed to load settings');
      });
  };

  const handleSettings = () => {
    setShowModal(true);
    loadModalData();
  };

  const updateConfigField = (fieldKey, value) => {
    setConfig((current) => ({ ...current, [fieldKey]: value }));
  };

  const mapValuesToOptions = (options, values = []) => (
    (values || [])
      .map((value) => options.find((option) => option.value === value))
      .filter(Boolean)
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = {
      ...config,
      plant_ids: (config.plant_ids || []).map((value) => Number(value)),
      schedule_statuses: config.schedule_statuses || [],
      record_statuses: config.record_statuses || [],
      enabled_date_ranges: schema?.toolbarDateRanges || [],
      default_date_range: config.default_date_range || 'current_month',
    };

    saveListViewSettings(cookies, viewKey, payload)
      .then((response) => {
        setShowModal(false);
        if (onSaved) {
          onSaved(response.config || payload);
        }
      })
      .catch((error) => {
        displayError(error.response?.data ?? error.message, 'Save Failed');
      });
  };

  if (!cookies.myToken || !viewKey || !schema) {
    return null;
  }

  return (
    <>
      <SettingsIcon onClickHandler={handleSettings} variant="inline" />

      {showModal && (
        <>
          <div className="list-settings-overlay" onClick={() => setShowModal(false)} />
          <div className="list-settings-modal" role="dialog" aria-modal="true">
            <div className="list-settings-header">
              <h2>General Settings</h2>
              <button
                type="button"
                className="list-settings-close"
                aria-label="Close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="list-settings-body">
                <div className="list-settings-panel">
                  <h3 className="list-settings-panel-title">{schema.sectionTitle}</h3>

                  {schema.statusOptions && (
                    <div className="list-settings-row">
                      <label htmlFor="schedule-status">Job Status</label>
                      <ReactSelect
                        inputId="schedule-status"
                        options={schema.statusOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option: CheckboxOption }}
                        isClearable
                        value={mapValuesToOptions(schema.statusOptions, config.schedule_statuses)}
                        onChange={(selected) => updateConfigField(
                          'schedule_statuses',
                          (selected || []).map((item) => item.value),
                        )}
                        styles={mandatorySelectStyles}
                        placeholder="Select job status"
                      />
                    </div>
                  )}

                  {schema.recordStatusOptions && (
                    <div className="list-settings-row">
                      <label htmlFor="record-status">
                        {viewKey === 'work_schedule' ? 'Record Status' : 'Status'}
                      </label>
                      <ReactSelect
                        inputId="record-status"
                        options={schema.recordStatusOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option: CheckboxOption }}
                        isClearable
                        value={mapValuesToOptions(schema.recordStatusOptions, config.record_statuses)}
                        onChange={(selected) => updateConfigField(
                          'record_statuses',
                          (selected || []).map((item) => item.value),
                        )}
                        styles={mandatorySelectStyles}
                        placeholder="Select status"
                      />
                    </div>
                  )}

                  {schema.showPlantFilter !== false && (
                  <div className="list-settings-row">
                    <label htmlFor="plant-select">Plant</label>
                    <ReactSelect
                      inputId="plant-select"
                      options={plantOptions}
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{ Option: CheckboxOption }}
                      isClearable
                      value={plantOptions.filter((option) => (config.plant_ids || []).includes(option.value))}
                      onChange={(selected) => updateConfigField(
                        'plant_ids',
                        (selected || []).map((item) => item.value),
                      )}
                      styles={mandatorySelectStyles}
                      placeholder="Select plants"
                    />
                  </div>
                  )}

                  <div className="list-settings-row">
                    <label htmlFor="date-range">Date Range</label>
                    <ReactSelect
                      inputId="date-range"
                      options={schema.dateRangeOptions}
                      value={schema.dateRangeOptions.find(
                        (option) => option.value === (config.default_date_range || 'current_month'),
                      ) || null}
                      onChange={(selected) => updateConfigField('default_date_range', selected?.value || 'current_month')}
                      styles={dateRangeSelectStyles}
                      placeholder="Select date range"
                    />
                  </div>

                  {config.default_date_range === 'custom' && (
                    <div className="list-settings-row">
                      <label>Custom Range</label>
                      <div className="list-settings-custom-dates">
                        <DatePicker
                          format="DD-MM-YYYY"
                          placeholder="From date"
                          allowClear
                          getCalendarContainer={() => document.body}
                          dropdownClassName="list-settings-datepicker-popup"
                          value={config.custom_from_date ? moment(config.custom_from_date, 'DD-MM-YYYY', true) : null}
                          onChange={(value) => updateConfigField(
                            'custom_from_date',
                            value ? value.format('DD-MM-YYYY') : null,
                          )}
                        />
                        <DatePicker
                          format="DD-MM-YYYY"
                          placeholder="Upto date"
                          allowClear
                          getCalendarContainer={() => document.body}
                          dropdownClassName="list-settings-datepicker-popup"
                          value={config.custom_upto_date ? moment(config.custom_upto_date, 'DD-MM-YYYY', true) : null}
                          onChange={(value) => updateConfigField(
                            'custom_upto_date',
                            value ? value.format('DD-MM-YYYY') : null,
                          )}
                        />
                      </div>
                    </div>
                  )}

                  <div className="list-settings-row">
                    <label>Resolved Range</label>
                    <div className="list-settings-resolved">{resolvedRangeText}</div>
                  </div>
                </div>
              </div>

              <div className="list-settings-footer">
                <button type="submit" className="list-settings-btn-update">Update</button>
                <button
                  type="button"
                  className="list-settings-btn-close"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
}

export default ListViewSettings;
